import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Table, Form } from 'react-bootstrap';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import data from '../../assets/data/zeitverlaufData.json';
import '../../App.css';
import { DATENSTAND } from "../../config/constants";
import { SEX_COLORS } from "../../config/colors";

// import the shared utility
import { exportToExcel } from '../../utils/exportExcel';

// 🔹 no suppression: every number is shown
const MIN_SUPPRESS_COUNT = 0;

const toNum = (v) => {
  if (v === null || v === undefined || v === '' || v === 'NA') return null;

  if (typeof v === "string") {
    // allow "12,4" or "5" etc.
    const cleaned = v.replace(",", ".").trim();
    const n = Number(cleaned);
    return Number.isNaN(n) ? null : n;
  }

  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};
const nfRate1 = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});


// grouping helpers
const GESAMT_ORDER = ['C99', 'C77', 'C98', 'D50', 'D51', 'D11'];

const parseCode = (code) => {
  if (!code || code === 'NA') return null;
  const m = String(code).match(/^[CD]\d{2}/i);
  return m ? m[0].toUpperCase() : null;
};

const letter = (c) => (c ? c[0] : null);
const number = (c) => (c ? parseInt(c.slice(1), 10) : NaN);

const groupOf = (code) => {
  const c = parseCode(code);
  if (c && GESAMT_ORDER.includes(c)) return 'GESAMT';
  if (c && letter(c) === 'C' && number(c) <= 80) return 'SOLIDE';
  if (c && letter(c) === 'C' && number(c) >= 81 && number(c) <= 96) return 'HAEM';
  if (c && letter(c) === 'D' && number(c) >= 0 && number(c) <= 9) return 'CIS';
  return 'BENIGN_UNGROUPED';
};

const groupLabel = {
  GESAMT: 'Gesamt',
  SOLIDE: 'Solide bösartige Neubildungen',
  HAEM: 'Hämatologische Erkrankungen (C81–C96)',
  CIS: 'Carcinoma in situ (D00–D09)',
  BENIGN_UNGROUPED: 'Meldepflichtige gutartige Neubildungen & ungruppiert',
};

const groupOrderIndex = (g) =>
  ({ GESAMT: 0, SOLIDE: 1, HAEM: 2, CIS: 3, BENIGN_UNGROUPED: 4 }[g] ?? 99);

const sortInGroup = (a, b, groupKey) => {
  const ca = parseCode(a.code);
  const cb = parseCode(b.code);

  if (groupKey === 'GESAMT') {
    return GESAMT_ORDER.indexOf(ca) - GESAMT_ORDER.indexOf(cb);
  }

  const la = letter(ca) || 'Z';
  const lb = letter(cb) || 'Z';
  if (la !== lb) return la.localeCompare(lb);

  const na = number(ca);
  const nb = number(cb);
  if (Number.isNaN(na) && Number.isNaN(nb)) return 0;
  if (Number.isNaN(na)) return 1;
  if (Number.isNaN(nb)) return -1;
  return na - nb;
};

// 🔹 mark sex-specific entities: only one sex should be shown
const getAllowedSexForEntit = (entit) => {
  const name = (entit || "").toLowerCase();

  // men only
  if (name.includes("prostata")) return "M";

  // women only
  if (
    name.includes("gebärmutterkörper") ||
    name.includes("gebaermutterkoerper") ||
    name.includes("gebarmutterkorper")
  ) return "W";

 if (
    name.includes("eierstöcke")
  ) return "W";

  if (
    name.includes("penis")
  ) return "M";

   if (
    name.includes("hoden")
  ) return "M";


   if (
    name.includes("sonstige weibliche geschlechtsorgane")
  ) return "W";

     if (
    name.includes("sonstige männliche geschlechtsorgane")
  ) return "M";



  if (
    name.includes("gebärmutterhals") ||
    name.includes("gebaermutterhals") ||
    name.includes("gebarmutterhals")
  ) return "W";

  if (name.includes("vulva")) return "W";
  if (name.includes("zervix")) return "W";

  // all other entities: both sexes allowed (if data exists)
  return null;
};

const Zeitverlauf = () => {
  const [selectedEntit, setSelectedEntit] = useState('');
  const [filteredData, setFilteredData] = useState([]);

  // entit list
  const uniqueEntit = useMemo(() => {
    const m = new Map();
    data.forEach((d) => {
      if (!m.has(d.entit)) m.set(d.entit, d.ICDCode);
    });
    return Array.from(m.entries()).map(([entit, rawCode]) => {
      const code = parseCode(rawCode);
      return {
        entit,
        code,
        display: entit,
        group: groupOf(code),
      };
    });
  }, []);

  // group + sort
  const groupedOptions = useMemo(() => {
    const byGroup = new Map();
    uniqueEntit.forEach((o) => {
      const g = o.group;
      if (!byGroup.has(g)) byGroup.set(g, []);
      byGroup.get(g).push(o);
    });

    return Array.from(byGroup.entries())
      .sort((a, b) => groupOrderIndex(a[0]) - groupOrderIndex(b[0]))
      .map(([g, items]) => ({
        key: g,
        label: groupLabel[g] || g,
        options: items.sort((a, b) => sortInGroup(a, b, g)),
      }));
  }, [uniqueEntit]);

  // filter dataset
  useEffect(() => {
    setFilteredData(data.filter((d) => d.entit === selectedEntit));
  }, [selectedEntit]);

  // default selection
  useEffect(() => {
    if (!selectedEntit && groupedOptions.length) {
      const firstGroup = groupedOptions.find((g) => g.options.length > 0);
      if (firstGroup) setSelectedEntit(firstGroup.options[0].entit);
    }
  }, [groupedOptions, selectedEntit]);

  // 🔹 determine whether entity is sex-specific
  const allowedSex = useMemo(
    () => getAllowedSexForEntit(selectedEntit),
    [selectedEntit]
  );

  // available sexes (respect sex-specific restriction)
  const hasM = useMemo(() => {
    if (allowedSex === "W") return false; // women-only entity -> hide Männer
    return filteredData.some((d) => d.SEX === 'M');
  }, [filteredData, allowedSex]);

  const hasW = useMemo(() => {
    if (allowedSex === "M") return false; // men-only entity -> hide Frauen
    return filteredData.some((d) => d.SEX === 'W');
  }, [filteredData, allowedSex]);

  // timeline years
  const years = useMemo(() => {
    const ys = [...new Set(filteredData.map((d) => String(d.diayear)))];
    return ys.sort((a, b) => parseInt(a) - parseInt(b));
  }, [filteredData]);

  // merge years/sex
  const mergedData = useMemo(() => {
    const byYearSex = (year, sex) =>
      filteredData.find((d) => String(d.diayear) === String(year) && d.SEX === sex);

    return years.map((year) => {
      const m = byYearSex(year, 'M');
      const w = byYearSex(year, 'W');

      const mCount = m ? toNum(m.n_wob) : null;
      const wCount = w ? toNum(w.n_wob) : null;

      const mUnder5 = mCount !== null && mCount > 0 && mCount < MIN_SUPPRESS_COUNT;
      const wUnder5 = wCount !== null && wCount > 0 && wCount < MIN_SUPPRESS_COUNT;

      return {
        diayear: parseInt(year, 10),

        n_wob_m_plot: mUnder5 ? 0 : mCount ?? null,
        n_wob_w_plot: wUnder5 ? 0 : wCount ?? null,

        n_wob_m_display: mUnder5 ? '<5' : mCount ?? null,
        n_wob_w_display: wUnder5 ? '<5' : wCount ?? null,

        altersInz_m: m ? toNum(m.altersInz) : null,
        altersInz_w: w ? toNum(w.altersInz) : null,

        Inz_m: m ? toNum(m.Inz) : null,
        Inz_w: w ? toNum(w.Inz) : null,

        n_dcn_m: m ? m.n_dcn : null,
        n_bob_m: m ? m.n_bob : null,

        n_dcn_w: w ? w.n_dcn : null,
        n_bob_w: w ? w.n_bob : null,
      };
    });
  }, [filteredData, years]);

  // build table rows
  const tableRows = useMemo(() => {
    const rows = [];
    mergedData.forEach((d) => {
      if (hasM) {
        rows.push({
          sex: 'M',
          year: d.diayear,
          n_wob_display: d.n_wob_m_display,
          n_dcn: d.n_dcn_m ?? '–',
          n_bob: d.n_bob_m ?? '–',
         Inz: d.Inz_m != null ? nfRate1.format(d.Inz_m) : '–',
          altersInz: d.altersInz_m != null ? nfRate1.format(d.altersInz_m) : '–',

        });
      }
      if (hasW) {
        rows.push({
          sex: 'W',
          year: d.diayear,
          n_wob_display: d.n_wob_w_display,
          n_dcn: d.n_dcn_w ?? '–',
          n_bob: d.n_bob_w ?? '–',
          Inz: d.Inz_w != null ? nfRate1.format(d.Inz_w) : '–',
altersInz: d.altersInz_w != null ? nfRate1.format(d.altersInz_w) : '–',

        });
      }
    });
    return rows;
  }, [mergedData, hasM, hasW]);

  // use shared export util
  const handleExport = () => {
    const sheetData = tableRows.map((row) => ({
      Jahr: row.year,
      Geschlecht: row.sex === 'M' ? 'Männer' : 'Frauen',
      'Anzahl Neuerkrankte (Wohnortbezug)': row.n_wob_display,
      'Anzahl von DCN-Fällen': row.n_dcn,
      'Anzahl Neuerkrankte (Behandlungsortbezug)': row.n_bob,
      'Rohe Erkrankungsrate pro 100.000 Einwohner': row.Inz,
      'ASR: Altersstandardisierte Rate pro 100.000 Einwohner': row.altersInz,
    }));

    exportToExcel({
      filename: 'zeitverlauf',
      sheetName: 'Zeitverlauf-Daten',
      data: sheetData,
      metadata: [
        ['Neuerkrankungen im Zeitverlauf in Sachsen-Anhalt'],
        ['Krebsregister Sachsen-Anhalt https://www.kr-lsa.de'],
        [`Datenstand: ${DATENSTAND}`],
        [`Export am: ${new Date().toLocaleString('de-DE')}`],
      ]
    });
  };

  return (
    <Container className="mt-4">
      <h2 className="section-title">Neuerkrankungen im Zeitverlauf</h2>
      <div className="section-title-underline"></div>
      <div className="aktuelles-text" style={{ marginTop: "0.5rem" }}>
        <p>
          Auf dieser Seite sind die Neuerkrankungen dargestellt. Gezeigt wird jeweils die absolute Anzahl an Neuerkrankungen im Land Sachsen-Anhalt sowie die altersstandardisierte Inzidenzrate nach altem Europastandard pro 100.000 Einwohner im zeitlichen Verlauf der vergangenen Jahre. 
          In der untenstehenden Tabelle können Sie weitere detaillierte Angaben zu den Neuerkrankungen ablesen.
        </p>
        <p>
          Über das Auswahlmenü im oberen Teil der Seite, 
          haben Sie die Möglichkeit, die Neuerkrankungsraten für einzelne Entitäten einzustellen.
        </p>
      </div>

      <Form.Group controlId="entitSelect" className="mb-4">
        <Form.Label>Krebs-Auswahl:</Form.Label>
        <Form.Select
          value={selectedEntit}
          onChange={(e) => setSelectedEntit(e.target.value)}
        >
          {groupedOptions.map((group) => (
            <optgroup key={group.key} label={group.label}>
              {group.options.map((o) => (
                <option key={o.entit} value={o.entit}>
                  {o.display}
                </option>
              ))}
            </optgroup>
          ))}
        </Form.Select>
      </Form.Group>

      <button onClick={handleExport} className="top-btn mb-3">
        📥 Daten als Excel exportieren
      </button>

      <Row>
        <Col md={6}>
          <h5>Fallzahlen (Wohnortbezug)</h5>
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={mergedData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="diayear" />
              <YAxis />
              <Tooltip
  formatter={(value, name) => {
    const num = Number(
      typeof value === "string" ? value.replace(",", ".") : value
    );

    if (Number.isNaN(num)) {
      return [value, name]; // fall back to whatever it was
    }

    return [nfRate1.format(num), name]; // e.g. 5 → 5,0
  }}
/>

              <Legend />
             {hasM && (
  <Line
    type="monotone"
    dataKey="n_wob_m_plot"
    stroke={SEX_COLORS.M}
    strokeWidth={3}
    name="Männer"
  />
)}

{hasW && (
  <Line
    type="monotone"
    dataKey="n_wob_w_plot"
    stroke={SEX_COLORS.W}
    strokeWidth={3}
    name="Frauen"
  />
)}
            </LineChart>
          </ResponsiveContainer>
        </Col>

        <Col md={6}>
          <h5>Altersstandardisierte Inzidenzraten</h5>
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={mergedData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="diayear" />
              <YAxis />
          <Tooltip
  formatter={(value, name) => {
    const num = Number(
      typeof value === "string" ? value.replace(",", ".") : value
    );

    if (Number.isNaN(num)) {
      return [value, name]; // fall back to whatever it was
    }

    return [nfRate1.format(num), name]; // e.g. 5 → 5,0
  }}
/>
              <Legend />
            {hasM && (
  <Line
    type="monotone"
    dataKey="altersInz_m"
    stroke={SEX_COLORS.M}
    strokeWidth={3}
    name="Männer"
  />
)}

{hasW && (
  <Line
    type="monotone"
    dataKey="altersInz_w"
    stroke={SEX_COLORS.W}
    strokeWidth={3}
    name="Frauen"
  />
)}
            </LineChart>
          </ResponsiveContainer>
        </Col>
      </Row>

      <h5 className="mt-5">Fallzahlen und Altersstandardisierte Raten in Sachsen-Anhalt</h5>

      <Table responsive className="zeitverlauf-table text-center align-middle">
        <thead>
          <tr>
            <th>Geschlecht</th>
            <th>Jahr</th>
            <th>Anzahl Neuerkrankte (Wohnortbezug)</th>
            <th>Anzahl von DCN-Fällen</th>
            <th>Anzahl Neuerkrankte (Behandlungsortbezug)</th>
            <th>Rohe Erkrankungsrate pro 100.000 Einwohner</th>
            <th>Altersstandardisierte Rate (ASR) pro 100.000 Einwohner</th>
          </tr>
        </thead>
        <tbody>
          {tableRows.map((row, i) => (
            <tr key={`${row.sex}-${row.year}-${i}`}>
              <td>{row.sex === 'M' ? 'Männer' : 'Frauen'}</td>
              <td>{row.year}</td>
              <td>{row.n_wob_display}</td>
              <td>{row.n_dcn}</td>
              <td>{row.n_bob}</td>
              <td>{row.Inz}</td>
              <td>{row.altersInz}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Container>
  );
};

export default Zeitverlauf;


